import type { CSSProperties } from 'react';

import classNames from 'classnames';

import { useAnchorable } from '../../hooks/useAnchorable';

import { Thumbnail } from './components/Thumbnail';

import { appendProtocol } from '../../utils/link';

import {
  articleContent,
  articleWrapper,
  byline,
  categoriesContainer,
  category,
  cta,
  ctaContainer,
  ctaLink as ctaLinkStyle,
  divider,
  dividerContainer,
  heading,
  paragraph,
  pubDate,
  rssContainer,
  rssTextBox,
} from './Rss.css';
import type { TPostRssElement, TPostRssProps } from './Rss.types';

const rssThumbnailPositions = {
  LEFT: 'Left of content',
  RIGHT: 'Right of content',
  TOP: 'Above Title',
  BOTTOM: 'Below Title',
  ALTERNATING_HORIZONTAL: 'Alternating Horizontally',
};

function getNumberOfColumns(entryLayout: string | null | undefined): number {
  if (!entryLayout) return 1;

  const columnCount = Number.parseInt(entryLayout.replace('-col', ''), 10);
  return Number.isNaN(columnCount) ? 1 : columnCount;
}

function extractFirstImage(htmlString: string) {
  if (!htmlString) return null;

  const match = htmlString.match(/<img[^>]+src="([^">]+)"/);
  return match ? match[1] : null;
}

type ArticleWithOptionalMedia = {
  thumbnail?: string;
  content?: string;
};

const getThumbnailSrc = (article: ArticleWithOptionalMedia) => {
  if (article.thumbnail) {
    return article.thumbnail;
  }
  return extractFirstImage(article.content ?? '') || '';
};

const thumbnailIsHorizontal = (thumbnailPosition: string) => {
  return [
    rssThumbnailPositions.LEFT,
    rssThumbnailPositions.RIGHT,
    rssThumbnailPositions.ALTERNATING_HORIZONTAL,
  ].includes(thumbnailPosition);
};

function optionalPxFontSizeStyle(px: unknown): CSSProperties | undefined {
  if (typeof px !== 'number' || !Number.isFinite(px) || px <= 0) return undefined;
  return { fontSize: `${Math.round(px)}px` };
}

function rssSettingsFontSize(
  settings: TPostRssElement['attrs']['settings'] | undefined,
  camelKey: 'dateAuthorFontSize' | 'descriptionFontSize',
  snakeKey: string,
): CSSProperties | undefined {
  if (!settings) return undefined;
  const raw = (settings as Record<string, unknown>)[camelKey] ?? (settings as Record<string, unknown>)[snakeKey];
  return optionalPxFontSizeStyle(raw);
}

/** Matches email `rss_text_box` / Swarm Write preview (`RssView` `rssTextBoxPaddingStyle`). */
function rssTextBoxPaddingStyle(settings: TPostRssElement['attrs']['settings'] | undefined): CSSProperties | undefined {
  if (!settings) return undefined;
  const rawSettings = settings as Record<string, unknown>;
  const side = (value: unknown) => {
    const n = typeof value === 'number' && Number.isFinite(value) ? Math.trunc(value) : 0;
    return n > 0 ? n : 0;
  };
  const useIndividual =
    settings.textBoxUseIndividualPadding === true || rawSettings.text_box_use_individual_padding === true;
  let top = 0;
  let right = 0;
  let bottom = 0;
  let left = 0;
  if (useIndividual) {
    top = side(settings.textBoxPaddingTop ?? rawSettings.text_box_padding_top);
    right = side(settings.textBoxPaddingRight ?? rawSettings.text_box_padding_right);
    bottom = side(settings.textBoxPaddingBottom ?? rawSettings.text_box_padding_bottom);
    left = side(settings.textBoxPaddingLeft ?? rawSettings.text_box_padding_left);
  } else {
    const u = side(settings.textBoxPadding ?? rawSettings.text_box_padding);
    top = right = bottom = left = u;
  }
  if (top === 0 && right === 0 && bottom === 0 && left === 0) {
    return undefined;
  }
  return {
    boxSizing: 'border-box',
    paddingTop: top,
    paddingRight: right,
    paddingBottom: bottom,
    paddingLeft: left,
  };
}

export const PostRss = (props: TPostRssProps) => {
  const { element, rewriteImageSrc } = props;
  const { attrs } = element;

  const { anchorAttributes } = useAnchorable(attrs);

  const { data, settings } = attrs;
  const entryLayout = attrs.entryLayout ?? '1-col';
  const effectiveNumberOfColumns = thumbnailIsHorizontal(settings?.thumbnailPosition || '')
    ? 1
    : getNumberOfColumns(entryLayout);
  const sanitizedImageWidthPercentage =
    typeof settings?.imageWidthPercentage === 'number' && Number.isFinite(settings.imageWidthPercentage)
      ? Math.max(20, Math.min(60, Math.round(settings.imageWidthPercentage)))
      : 50;

  const thumbnailAsLink =
    settings?.ctaStyle === 'Thumbnail as link' ||
    settings?.ctaStyle === 'Title and Thumbnail as link' ||
    settings?.ctaStyle === 'Title, Thumbnail and Button as CTA' ||
    settings?.ctaStyle === 'Title, Thumbnail and Link as CTA';

  const titleAsLink =
    settings?.ctaStyle === 'Title as link' ||
    settings?.ctaStyle === 'Title and Thumbnail as link' ||
    settings?.ctaStyle === 'Title, Thumbnail and Button as CTA' ||
    settings?.ctaStyle === 'Title, Thumbnail and Link as CTA';

  const showTitleAboveThumbnail = settings?.thumbnailPosition === rssThumbnailPositions.BOTTOM;

  const ctaButton = settings?.ctaStyle === 'CTA button' || settings?.ctaStyle === 'Title, Thumbnail and Button as CTA';
  const ctaLink = settings?.ctaStyle === 'CTA link' || settings?.ctaStyle === 'Title, Thumbnail and Link as CTA';

  const articleWrapperClassNames = (index: number): string => {
    if (settings?.thumbnailPosition === rssThumbnailPositions.ALTERNATING_HORIZONTAL) {
      if (settings?.rowLayout !== 'alternating-left') {
        return index % 2 === 0 ? 'flex-col sm:flex-row-reverse' : 'flex-col sm:flex-row';
      }
      return index % 2 === 0 ? 'flex-col sm:flex-row' : 'flex-col sm:flex-row-reverse';
    }
    if (settings?.thumbnailPosition === rssThumbnailPositions.LEFT) {
      return 'flex-col sm:flex-row';
    }
    if (settings?.thumbnailPosition === rssThumbnailPositions.RIGHT) {
      return 'flex-col sm:flex-row-reverse';
    }

    return 'flex-col';
  };

  const horizontalImageSpacingClassNames = (index: number): string => {
    if (settings?.thumbnailPosition === rssThumbnailPositions.ALTERNATING_HORIZONTAL) {
      if (settings?.rowLayout !== 'alternating-left') {
        return index % 2 === 0 ? 'sm:pl-[--wt-hyperlink-padding]' : 'sm:pr-[--wt-hyperlink-padding]';
      }
      return index % 2 === 0 ? 'sm:pr-[--wt-hyperlink-padding]' : 'sm:pl-[--wt-hyperlink-padding]';
    }
    if (settings?.thumbnailPosition === rssThumbnailPositions.LEFT) {
      return 'sm:pr-[--wt-hyperlink-padding]';
    }
    if (settings?.thumbnailPosition === rssThumbnailPositions.RIGHT) {
      return 'sm:pl-[--wt-hyperlink-padding]';
    }

    return '';
  };

  const imageVerticalAlignmentClassName = (): string => {
    if (!thumbnailIsHorizontal(settings?.thumbnailPosition || '')) return '';

    if (settings?.imageVerticalAlign === 'top') return 'sm:self-start';
    if (settings?.imageVerticalAlign === 'bottom') return 'sm:self-end';

    return 'sm:self-center';
  };

  const textVerticalAlignmentClassName = (): string => {
    if (!thumbnailIsHorizontal(settings?.thumbnailPosition || '')) return '';

    if (settings?.textVerticalAlign === 'top') return 'sm:self-start';
    if (settings?.textVerticalAlign === 'bottom') return 'sm:self-end';

    return 'sm:self-center';
  };

  const textAlignCss =
    settings?.textAlign === 'center' || settings?.textAlign === 'right' ? settings.textAlign : 'left';
  const categoriesJustifyCss =
    textAlignCss === 'center' ? 'center' : textAlignCss === 'right' ? 'flex-end' : 'flex-start';

  const ctaAlignCss = (() => {
    const c = settings?.ctaAlignment?.toLowerCase();
    if (c === 'center' || c === 'right') return c;
    return 'left';
  })();

  const titleFontSizeStyle = optionalPxFontSizeStyle(settings?.titleFontSize);
  const dateAuthorFontSizeStyle = rssSettingsFontSize(settings, 'dateAuthorFontSize', 'date_author_font_size');
  const descriptionFontSizeStyle = rssSettingsFontSize(settings, 'descriptionFontSize', 'description_font_size');
  const textBoxPaddingStyle = rssTextBoxPaddingStyle(settings);

  return (
    <div
      {...anchorAttributes}
      className={rssContainer}
      style={
        {
          '--number-of-columns': effectiveNumberOfColumns,
          '--cta-alignment': ctaAlignCss,
          '--rss-image-width': `${sanitizedImageWidthPercentage}%`,
          '--rss-text-align': textAlignCss,
          '--rss-categories-justify': categoriesJustifyCss,
        } as CSSProperties
      }
    >
      {data?.content?.slice(0, settings?.entriesToShow || 1)?.map((article, index) => {
        const thumbnailSrc = getThumbnailSrc(article);
        const articleThumbnailSrc = thumbnailSrc && rewriteImageSrc ? rewriteImageSrc(thumbnailSrc) : thumbnailSrc;
        const articleLink = article.link ? appendProtocol(article.link) : article.link;

        return (
          <div key={article.link}>
            {settings?.showDividers &&
              index > 0 &&
              (thumbnailIsHorizontal(settings?.thumbnailPosition || '') || entryLayout === '1-col') && (
                <div className={dividerContainer}>
                  <div className={divider} />
                </div>
              )}
            <div
              className={classNames(
                articleWrapper,
                articleWrapperClassNames(index),
                thumbnailIsHorizontal(settings?.thumbnailPosition || '') ? 'sm:items-start' : '',
              )}
            >
              {settings?.displayThumbnail &&
              articleThumbnailSrc &&
              thumbnailIsHorizontal(settings?.thumbnailPosition || '') ? (
                <div
                  className={classNames(
                    'w-full sm:shrink-0 sm:basis-[var(--rss-image-width)] sm:w-[var(--rss-image-width)]',
                    imageVerticalAlignmentClassName(),
                    horizontalImageSpacingClassNames(index),
                  )}
                >
                  <Thumbnail
                    asLink={thumbnailAsLink}
                    articleLink={articleLink}
                    articleTitle={article.title}
                    articleThumbnailSrc={articleThumbnailSrc}
                  />
                </div>
              ) : null}
              <div className={classNames(articleContent, textVerticalAlignmentClassName())}>
                <div className={rssTextBox} style={textBoxPaddingStyle}>
                  {thumbnailIsHorizontal(settings?.thumbnailPosition || '') &&
                    settings?.displayCategories &&
                    article.categories &&
                    article.categories.length > 0 && (
                      <div className={categoriesContainer}>
                        {article.categories.map((c) => (
                          <span key={c} className={category}>
                            {c}
                          </span>
                        ))}
                      </div>
                    )}
                  {settings?.displayTitle &&
                  (showTitleAboveThumbnail || thumbnailIsHorizontal(settings?.thumbnailPosition || '')) ? (
                    titleAsLink && !!article.link ? (
                      <a href={articleLink} target="_blank" rel="noreferrer" data-skip-tracking="true">
                        <h2 className={heading} style={titleFontSizeStyle}>
                          {article.title}
                        </h2>
                      </a>
                    ) : (
                      <h2 className={heading} style={titleFontSizeStyle}>
                        {article.title}
                      </h2>
                    )
                  ) : null}
                  {settings?.displayThumbnail &&
                  articleThumbnailSrc &&
                  !thumbnailIsHorizontal(settings?.thumbnailPosition || '') ? (
                    <Thumbnail
                      asFullWidth
                      asLink={thumbnailAsLink}
                      articleLink={articleLink}
                      articleTitle={article.title}
                      articleThumbnailSrc={articleThumbnailSrc}
                    />
                  ) : null}

                  {!thumbnailIsHorizontal(settings?.thumbnailPosition || '') &&
                    settings?.displayCategories &&
                    article.categories &&
                    article.categories.length > 0 && (
                      <div className={categoriesContainer}>
                        {article.categories.map((c) => (
                          <span key={c} className={category}>
                            {c}
                          </span>
                        ))}
                      </div>
                    )}

                  {settings?.displayTitle &&
                  !showTitleAboveThumbnail &&
                  !thumbnailIsHorizontal(settings?.thumbnailPosition || '') ? (
                    titleAsLink && !!article.link ? (
                      <a href={articleLink} target="_blank" rel="noreferrer" data-skip-tracking="true">
                        <h2 className={heading} style={titleFontSizeStyle}>
                          {article.title}
                        </h2>
                      </a>
                    ) : (
                      <h2 className={heading} style={titleFontSizeStyle}>
                        {article.title}
                      </h2>
                    )
                  ) : null}
                  {settings?.displayAuthor && article.author && settings?.displayPubDate && article.pub_date && (
                    <p className={byline} style={dateAuthorFontSizeStyle}>
                      by {article.author}
                      <br />
                      {new Date(article.pub_date).toLocaleDateString('en-US', {
                        month: 'long',
                        day: 'numeric',
                        year: 'numeric',
                      })}
                    </p>
                  )}
                  {settings?.displayAuthor && article.author && !(settings?.displayPubDate && article.pub_date) && (
                    <p className={byline} style={dateAuthorFontSizeStyle}>
                      by {article.author}
                    </p>
                  )}
                  {settings?.displayPubDate && article.pub_date && !(settings?.displayAuthor && article.author) && (
                    <p className={pubDate} style={dateAuthorFontSizeStyle}>
                      {new Date(article.pub_date).toLocaleDateString('en-US', {
                        month: 'long',
                        day: 'numeric',
                        year: 'numeric',
                      })}
                    </p>
                  )}
                  {settings?.displayDescription && article.description && (
                    <p
                      className={paragraph}
                      style={descriptionFontSizeStyle}
                      dangerouslySetInnerHTML={{ __html: article.description }}
                    />
                  )}
                  {settings?.displayContent && article.content && (
                    <p className={paragraph} dangerouslySetInnerHTML={{ __html: article.content }} />
                  )}
                  {settings?.displayCta && (ctaButton || ctaLink) && article.link && (
                    <div className={ctaContainer}>
                      <a
                        href={articleLink}
                        target="_blank"
                        rel="noreferrer"
                        data-skip-tracking="true"
                        className={ctaLink ? ctaLinkStyle : undefined}
                      >
                        {ctaButton ? (
                          <button type="button" className={cta}>
                            {settings?.ctaText || ''}
                          </button>
                        ) : (
                          settings?.ctaText || ''
                        )}
                      </a>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};
